import {
  apiFetch,
  apiPatch,
  apiPut,
  apiPost,
  apiDelete,
} from '@/utils/fetchWrapper';
import {
  buildNominalsQueryString,
  buildPaginatedPath,
} from '@/utils/queryParams';
import {
  PaginatedNominalsResponse,
  NominalsFilters,
  Nominal,
  UpdateNominalRequest,
  NominalDetailResponse,
  UpdateNominalDetailRequest,
  NominalContact,
  CreateContactRequest,
  UpdateContactRequest,
  CreateNominalRequest,
} from '@/types/nominals';
import { NominalCallHistory } from '@/types/calls';

export const nominalsService = {
  async getNominals(
    filters: NominalsFilters = {}
  ): Promise<PaginatedNominalsResponse> {
    const url = buildPaginatedPath('/nominals/', buildNominalsQueryString(filters));
    return apiFetch<PaginatedNominalsResponse>(url);
  },

  async createNominal(nominalData: CreateNominalRequest): Promise<Nominal> {
    return apiPost<Nominal, CreateNominalRequest>('/nominals/', nominalData);
  },

  async updateNominal(
    nominalId: string,
    updates: UpdateNominalRequest
  ): Promise<Nominal> {
    try {
      return await apiPatch<Nominal, UpdateNominalRequest>(
        `/nominals/${nominalId}/`,
        updates
      );
    } catch (error) {
      // Fallback to PUT if PATCH fails
      return await apiPut<Nominal, UpdateNominalRequest>(
        `/nominals/${nominalId}/`,
        updates
      );
    }
  },

  async getNominal(nominalId: string): Promise<Nominal> {
    return apiFetch<Nominal>(`/nominals/${nominalId}/`);
  },

  async getNominalDetail(nominalId: string): Promise<NominalDetailResponse> {
    try {
      const nominal = await apiFetch<Nominal>(`/nominals/${nominalId}/`);

      const result: NominalDetailResponse = {
        nominal,
        contacts: {
          nominal_contacts: [],
          user_contacts: [],
          shared_contacts: [],
        },
        call_history: {
          recent_calls: [],
          total_calls: 0,
        },
      };

      const [nominalContacts, callHistory] = await Promise.allSettled([
        apiFetch<{ results: NominalContact[]; count: number }>(
          `/nominals/${nominalId}/contacts/`
        ),
        apiFetch<{ results: NominalCallHistory[]; count: number }>(
          `/nominal_call/?nominal=${nominalId}&page=1&page_size=20`
        ),
      ]);

      result.contacts = {
        nominal_contacts:
          nominalContacts.status === 'fulfilled'
            ? nominalContacts.value.results
            : [],
        user_contacts: [],
        shared_contacts: [],
      };

      result.call_history = {
        recent_calls:
          callHistory.status === 'fulfilled' ? callHistory.value.results : [],
        total_calls:
          callHistory.status === 'fulfilled' ? callHistory.value.count : 0,
      };

      return result;
    } catch (error) {
      throw error;
    }
  },

  async updateNominalDetail(
    nominalId: string,
    updates: UpdateNominalDetailRequest
  ): Promise<Nominal> {
    try {
      return await apiPatch<Nominal, UpdateNominalDetailRequest>(
        `/nominals/${nominalId}/`,
        updates
      );
    } catch (error) {
      // Fallback to PUT if PATCH fails
      return await apiPut<Nominal, UpdateNominalDetailRequest>(
        `/nominals/${nominalId}/`,
        updates
      );
    }
  },

  async getNominalContacts(nominalId: string): Promise<NominalContact[]> {
    const response = await apiFetch<{
      results: NominalContact[];
      count: number;
    }>(`/nominals/${nominalId}/contacts/`);
    return response.results;
  },

  async createNominalContact(
    nominalId: string,
    contact: CreateContactRequest
  ): Promise<NominalContact> {
    return await apiPost<NominalContact, CreateContactRequest>(
      `/nominals/${nominalId}/contacts/`,
      contact
    );
  },

  async updateNominalContact(
    nominalId: string,
    contactId: string,
    updates: UpdateContactRequest
  ): Promise<NominalContact> {
    return await apiPut<NominalContact, UpdateContactRequest>(
      `/nominals/${nominalId}/contacts/${contactId}/`,
      updates
    );
  },

  async deleteNominalContact(
    nominalId: string,
    contactId: string
  ): Promise<void> {
    await apiDelete(`/nominals/${nominalId}/contacts/${contactId}/`);
  },
};
